import React from 'react'
import { CheckSquare, Calendar, Users, Award, TrendingUp, UtensilsCrossed, Crown, Clock, AlertTriangle, Star, Zap, MessageSquare, Bell, GraduationCap } from 'lucide-react'

export interface DashboardTilesProps {
  onTileClick: (section: 'chores' | 'calendar' | 'school' | 'kids' | 'rewards' | 'analytics' | 'meals' | 'settings' | 'requests') => void
  showMealPlanning?: boolean
  hiddenTiles?: string[]
  stats?: {
    activeChores: number
    upcomingEvents: number
    activeKids: number
    pendingRewards: number
    needsVerification?: number
    overdue?: number
    completedToday?: number
    weeklyCompletionRate?: number
    nextEvent?: {
      title: string
      time: string
    }
    topKid?: {
      name: string
      points: number
    }
    totalFamilyPoints?: number
    tonightsDinner?: string
    kidRequests?: number
    pendingAvailability?: number
  }
}

interface StatItem {
  label: string
  value: string | number
  icon: React.ComponentType<{ className?: string }>
}

interface TileConfig {
  id: 'chores' | 'calendar' | 'school' | 'kids' | 'rewards' | 'analytics' | 'meals' | 'settings' | 'requests'
  icon: React.ComponentType<{ className?: string }>
  title: string
  gradient: string
  needsAttention?: boolean
  stats: StatItem[]
}

export const DashboardTiles: React.FC<DashboardTilesProps> = ({
  onTileClick,
  showMealPlanning = false,
  hiddenTiles = [],
  stats = {
    activeChores: 0,
    upcomingEvents: 0,
    activeKids: 0,
    pendingRewards: 0
  }
}) => {
  // Determine if tiles need attention
  const choresNeedAttention = (stats.needsVerification || 0) > 0 || (stats.overdue || 0) > 0
  const rewardsNeedAttention = (stats.pendingRewards || 0) > 0
  const requestsNeedAttention = (stats.kidRequests || 0) > 0 || (stats.pendingAvailability || 0) > 0

  const tiles: TileConfig[] = [
    {
      id: 'chores',
      icon: CheckSquare,
      title: 'Chores',
      gradient: 'from-blue-500 to-indigo-600',
      needsAttention: choresNeedAttention,
      stats: [
        { label: 'Active', value: stats.activeChores, icon: Clock },
        { label: 'Need Review', value: stats.needsVerification || 0, icon: AlertTriangle }
      ]
    },
    {
      id: 'calendar',
      icon: Calendar,
      title: 'Calendar',
      gradient: 'from-green-500 to-emerald-600',
      stats: [
        { label: 'Upcoming', value: stats.upcomingEvents, icon: Calendar }
      ]
    },
    {
      id: 'school',
      icon: GraduationCap,
      title: 'School',
      gradient: 'from-teal-500 to-sky-600',
      stats: [
        { label: 'Transport & Info', value: '', icon: GraduationCap }
      ]
    },
    {
      id: 'kids',
      icon: Users,
      title: 'Kids',
      gradient: 'from-purple-500 to-violet-600',
      stats: [
        { label: 'Family', value: stats.activeKids, icon: Users },
        { label: 'Points', value: stats.totalFamilyPoints || 0, icon: Star }
      ]
    },
    {
      id: 'rewards',
      icon: Award,
      title: 'Rewards',
      gradient: 'from-pink-500 to-rose-600',
      needsAttention: rewardsNeedAttention,
      stats: [
        { label: 'Pending', value: stats.pendingRewards, icon: Award }
      ]
    },
    {
      id: 'analytics',
      icon: TrendingUp,
      title: 'Stats',
      gradient: 'from-amber-500 to-orange-600',
      stats: [
        { label: 'Weekly', value: `${stats.weeklyCompletionRate || 0}%`, icon: TrendingUp },
        { label: 'Today', value: stats.completedToday || 0, icon: Zap }
      ]
    },
    {
      id: 'requests',
      icon: MessageSquare,
      title: 'Requests',
      gradient: 'from-cyan-500 to-teal-600',
      needsAttention: requestsNeedAttention,
      stats: [
        { label: 'Kid Requests', value: stats.kidRequests || 0, icon: Bell },
        { label: 'Availability', value: stats.pendingAvailability || 0, icon: Clock }
      ]
    }
  ]

  // Filter out tiles the user doesn't have permission for
  const visibleTiles = hiddenTiles.length > 0 ? tiles.filter(t => !hiddenTiles.includes(t.id)) : tiles

  if (showMealPlanning) {
    // Append meals at the end (Settings is no longer a tile — lives in the header cog).
    visibleTiles.push({
      id: 'meals',
      icon: UtensilsCrossed,
      title: 'Meals',
      gradient: 'from-orange-500 to-red-600',
      stats: [
        { label: 'Tonight', value: stats.tonightsDinner || 'No dinner planned', icon: UtensilsCrossed }
      ]
    })
  }

  return (
    <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
      {visibleTiles.map((tile) => {
        const Icon = tile.icon

        return (
          <button
            key={tile.id}
            onClick={() => onTileClick(tile.id)}
            className={`group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 ${
              tile.needsAttention ? 'ring-2 ring-red-400 ring-offset-2 ring-offset-white shadow-red-200/50' : ''
            }`}
          >
            <div className={`absolute inset-0 bg-gradient-to-br ${tile.gradient} opacity-90 group-hover:opacity-100 transition-opacity`} />

            {/* Attention indicator */}
            {tile.needsAttention && (
              <div className="absolute top-4 right-4 z-20">
                <span className="flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500" />
                </span>
              </div>
            )}

            <div className="relative p-4 md:p-6 text-white">
              {/* Icon and main stat */}
              <div className="flex items-center justify-between mb-2 md:mb-4">
                <Icon className="w-8 md:w-10 h-8 md:h-10" />
                {typeof tile.stats[0]?.value === 'number' || (tile.stats[0]?.value !== '' && !isNaN(Number(tile.stats[0]?.value))) ? (
                  <div className="w-8 md:w-10 h-8 md:h-10 rounded-full bg-white/20 flex items-center justify-center">
                    <span className="text-base md:text-lg font-bold">
                      {tile.stats[0]?.value ?? 0}
                    </span>
                  </div>
                ) : (
                  <div className="w-8 md:w-10 h-8 md:h-10 rounded-full bg-white/20 flex items-center justify-center">
                    <Icon className="w-4 md:w-5 h-4 md:h-5" />
                  </div>
                )}
              </div>

              {/* Title */}
              <h3 className="text-base md:text-xl font-bold mb-2 md:mb-3">{tile.title}</h3>

              {/* Stats - matching kid dashboard style */}
              <div className="space-y-1 md:space-y-2">
                {tile.stats.map((stat, idx) => {
                  const StatIcon = stat.icon
                  return (
                    <div key={idx} className="flex items-center gap-1 md:gap-2 text-xs md:text-sm">
                      <StatIcon className="w-3 md:w-4 h-3 md:h-4 opacity-80" />
                      <span className="opacity-90">{stat.label}:</span>
                      <span className="font-semibold truncate">{stat.value}</span>
                    </div>
                  )
                })}
              </div>
            </div>
          </button>
        )
      })}
    </div>
  )
}
